import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EditOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Spin, Tabs, Tooltip } from 'antd';
import type { TabsProps } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { getMemberCardInfoApi, getMemberGradeListApi, getMemberInfoApi } from '@/api/member/member';
import { state } from '@/store';
import ChangeGradeDrawer from './components/ChangeGradeDrawer/ChangeGradeDrawer';
import ChangePointDrawer from './components/ChangePointDrawer/ChangePointDrawer';
import MemberInfoTab, { MemberInfoTabHandle } from './components/MemberInfoTab/MemberInfoTab';
import MemberJourneyTab from './components/MemberJourneyTab/MemberJourneyTab';
import OrderInfoTab from './components/OrderInfoTab/OrderInfoTab';
import PetInfoTab from './components/PetInfoTab/PetInfoTab';
import RightsGrantTab from './components/RightsGrantTab/RightsGrantTab';
import GrowUpTab from './components/GrowUpTab/GrowUpTab';
import OrderList from '@/pages/pointCenter/orderManage/orderList';
import styles from './index.module.scss';

const TAB_KEYS = {
  JOURNEY: 'journey',
  PET: 'pet',
  SHOP_ORDER: 'shopOrder',
  RIGHTS_ORDER: 'rightsOrder',
  MEMBER_INFO: 'memberInfo',
  RIGHTS_GRANT: 'rightsGrant',
  GROW_UP: 'growUp',
};

const MemberDetail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const snap = useSnapshot(state);
  const currentShopId = snap.currentShopId;

  const shopId =
    currentShopId || (typeof window !== 'undefined' ? Number(localStorage.getItem('currentShopId')) : null);
  const oneId = searchParams.get('oneId') || '';
  const memberCardId = searchParams.get('memberCardId');
  const shopUserId = searchParams.get('shopUserId');

  const memberInfoRef = useRef<MemberInfoTabHandle>(null);
  const [memberInfo, setMemberInfo] = useState<any>({});
  const [infoLoading, setInfoLoading] = useState(false);
  const [activeKey, setActiveKey] = useState(TAB_KEYS.JOURNEY);
  const [gradeOptions, setGradeOptions] = useState<{ label: string; value: string | number }[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContext, setDrawerContext] = useState<{
    oneId: string;
    mobile?: string;
    memberCardId?: string;
    currentLevelCode?: number | string;
    currentLevelName?: string;
    shopId?: string;
  }>();
  const [pointDrawerOpen, setPointDrawerOpen] = useState(false);
  const [pointDrawerContext, setPointDrawerContext] = useState<{
    oneId: string;
    memberCardId?: string;
    shopId?: string | number;
    channelType?: string;
    mobile?: string;
    platformId?: string;
    currentPoint?: number;
  }>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!shopId || !oneId) return;
    fetchMemberInfo();
    setGradeOptions([]);
  }, [shopId, oneId, memberCardId]);

  useEffect(() => {
    setActiveKey(TAB_KEYS.JOURNEY);
  }, [oneId, shopId, memberCardId]);

  const fetchMemberInfo = async () => {
    setInfoLoading(true);
    try {
      const res = await getMemberInfoApi({
        shopId,
        oneId,
        memberCardId,
      });
      if (res?.code === 200 || res?.code === 0) {
        setMemberInfo(res.data || {});
      }
    } catch (error) {
      console.error(error);
    } finally {
      setInfoLoading(false);
    }
  };

  const ensureGradeOptions = async () => {
    if (gradeOptions.length) return gradeOptions;
    try {
      const res = await getMemberGradeListApi({ shopId: shopId || snap.currentShopId });
      if (res?.code === 200) {
        const options = (res.data || []).map((item: any) => ({ label: item.gradeName, value: item.gradeCode }));
        setGradeOptions(options);
        return options;
      }
    } catch (error) {
      console.error(error);
    }
    return [];
  };

  const resolveCardContext = async () => {
    const snapshot = memberInfoRef.current?.getCardContext();
    if (snapshot?.cardList?.length) {
      const normalizedRouteId = memberCardId ? String(memberCardId) : undefined;
      const normalizedActiveId = snapshot.activeCardId ? String(snapshot.activeCardId) : undefined;
      const preferredId =
        normalizedActiveId ||
        normalizedRouteId ||
        (snapshot.cardList[0]?.memberCardId !== undefined ? String(snapshot.cardList[0].memberCardId) : undefined);
      const activeCard =
        snapshot.activeCard ||
        snapshot.cardList.find(card => String(card.memberCardId) === preferredId) ||
        snapshot.cardList[0];
      return { cardList: snapshot.cardList, activeCard };
    }
    try {
      const res = await getMemberCardInfoApi({ shopId, oneId });
      if (res?.code === 200 || res?.code === 0) {
        const list: any[] = res.data || [];
        const normalizedRouteId = memberCardId ? String(memberCardId) : undefined;
        const preferredId =
          normalizedRouteId || (list[0]?.memberCardId !== undefined ? String(list[0].memberCardId) : undefined);
        const activeCard = list.find((card: any) => String(card.memberCardId) === preferredId) || list[0];
        return { cardList: list, activeCard };
      }
    } catch (error) {
      console.error(error);
    }
    return { cardList: [], activeCard: undefined };
  };

  const memberMeta = useMemo(() => {
    return [
      { label: '平台ID', value: memberInfo.platformId || shopUserId || '-' },
      { label: '入会时间', value: memberInfo.createTime || '-' },
      {
        label: '会员等级',
        value: memberInfo.gradeName ? `${memberInfo.gradeName}` : '-',
        action: 'setLevel',
      },
      {
        label: '可用积分',
        value: memberInfo.totalPoint ?? '-',
        action: 'sendPoints',
      },
    ];
  }, [memberInfo, shopUserId]);

  const handleSetLevel = async () => {
    if (!memberInfo.oneId) return;
    const levelOptions = await ensureGradeOptions();
    setGradeOptions(levelOptions);
    const { activeCard, cardList } = await resolveCardContext();
    const cardId = activeCard?.memberCardId ?? cardList[0]?.memberCardId ?? memberCardId;
    const normalizedCardId = cardId !== undefined && cardId !== null ? String(cardId) : undefined;
    if (!normalizedCardId) return;
    const resolvedShopId = activeCard?.shopId || shopId || currentShopId;
    setDrawerContext({
      oneId: memberInfo.oneId,
      mobile: memberInfo.mobile,
      memberCardId: normalizedCardId,
      currentLevelCode: memberInfo.gradeCode,
      currentLevelName: memberInfo.gradeName || memberMeta.find(item => item.action === 'setLevel')?.value,
      shopId: resolvedShopId ? String(resolvedShopId) : undefined,
    });
    setDrawerOpen(true);
  };

  const handleSendPoints = async () => {
    if (!memberInfo.oneId) return;
    const { activeCard, cardList } = await resolveCardContext();
    const targetCard = activeCard || cardList[0] || (memberCardId ? { memberCardId: memberCardId, shopId } : null);
    if (!targetCard) return;
    setPointDrawerContext({
      oneId: memberInfo.oneId,
      memberCardId: targetCard?.memberCardId ? String(targetCard.memberCardId) : undefined,
      shopId: targetCard?.shopId || shopId || currentShopId,
      channelType: targetCard?.channelType || memberInfo.channelType,
      mobile: memberInfo.mixMobile || memberInfo.mobile,
      platformId: memberInfo.platformId,
      currentPoint: memberInfo.totalPoint,
    });
    setPointDrawerOpen(true);
  };

  const tabs: TabsProps['items'] = [
    {
      key: TAB_KEYS.JOURNEY,
      label: '会员履历',
      children: (
        <MemberJourneyTab
          active={activeKey === TAB_KEYS.JOURNEY}
          oneId={oneId}
          shopId={shopId}
        />
      ),
    },
    {
      key: TAB_KEYS.PET,
      label: '宠物信息',
      children: (
        <PetInfoTab
          oneId={oneId}
          active={activeKey === TAB_KEYS.PET}
        />
      ),
    },
    {
      key: TAB_KEYS.SHOP_ORDER,
      label: '会员订单',
      children: (
        <OrderInfoTab
          active={activeKey === TAB_KEYS.SHOP_ORDER}
          oneId={oneId}
          shopId={shopId}
        />
      ),
    },
    {
      key: TAB_KEYS.RIGHTS_ORDER,
      label: '权益订单',
      children: (
        <OrderList
          overrideShopId={shopId || undefined}
          fixedUserId={shopUserId}
          hideImportExport
          hideUserFilters
          noPagePadding
        />
      ),
    },
    {
      key: TAB_KEYS.MEMBER_INFO,
      label: '会员信息',
      forceRender: true,
      children: (
        <MemberInfoTab
          ref={memberInfoRef}
          active={activeKey === TAB_KEYS.MEMBER_INFO}
          oneId={oneId}
          shopId={shopId}
          memberCardId={memberCardId}
        />
      ),
    },
    {
      key: TAB_KEYS.RIGHTS_GRANT,
      label: '会员权益发放',
      children: (
        <RightsGrantTab
          active={activeKey === TAB_KEYS.RIGHTS_GRANT}
          shopId={shopId}
          oneId={oneId}
        />
      ),
    },
    {
      key: TAB_KEYS.GROW_UP,
      label: '成长礼',
      children: (
        <GrowUpTab
          active={activeKey === TAB_KEYS.GROW_UP}
          shopId={shopId}
          oneId={oneId}
          shopUserId={shopUserId}
        />
      ),
    },
  ];

  return (
    <div className={styles['member-detail-page']}>
      <div className={styles['member-detail-header']}>
        <div>
          <div className={styles['member-detail-title']}>会员详情</div>
          <div className={styles['member-detail-subtitle']}>OneID：{memberInfo.oneId || oneId}</div>
        </div>
      </div>
      <div className={styles['member-detail-body']}>
        <div className={styles['member-detail-left']}>
          <Spin spinning={infoLoading}>
            <div className={styles['member-card']}>
              <Avatar
                size={72}
                src={memberInfo.jdHeadImg || memberInfo.tmallHeadImg}
                icon={<UserOutlined />}
              />
              <div className={styles['member-card__name']}>{memberInfo.nickName || '会员'}</div>
              {/* <div className={styles['member-card__mobile']}>{memberInfo.mixMobile || '-'}</div> */}
              <div
                className={styles['member-card__meta']}
                style={{ padding: '20px 0' }}>
                <div>
                  <span className={styles['member-card__label']}>加密手机号：</span>
                  <span className={styles['member-card__value']}>{memberInfo.mobile}</span>
                </div>
              </div>
              <div className={styles['member-card__meta']}>
                {memberMeta.map(item => (
                  <div key={item.label}>
                    <span className={styles['member-card__label']}>{item.label}：</span>
                    <span className={styles['member-card__value']}>{item.value}</span>
                    {item.action === 'setLevel' && (
                      // <button
                      //   type="button"
                      //   onClick={handleSetLevel}>
                      //   设置等级
                      // </button>
                      <Tooltip title="设置等级">
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={handleSetLevel}></Button>
                      </Tooltip>
                    )}
                    {item.action === 'sendPoints' && (
                      // <button
                      //   type="button"
                      //   onClick={handleSendPoints}>
                      //   发积分
                      // </button>
                      <Tooltip title="发积分">
                        <Button
                          type="link"
                          size="small"
                          icon={<SendOutlined />}
                          onClick={handleSendPoints}></Button>
                      </Tooltip>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Spin>
        </div>
        <div className={styles['member-detail-right']}>
          <Tabs
            className={styles['member-detail-tabs']}
            items={tabs}
            activeKey={activeKey}
            onChange={setActiveKey}
          />
        </div>
      </div>
      <ChangeGradeDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        levelOptions={gradeOptions}
        oneId={drawerContext?.oneId || ''}
        mobile={drawerContext?.mobile}
        memberCardId={drawerContext?.memberCardId}
        currentLevelCode={drawerContext?.currentLevelCode}
        currentLevelName={drawerContext?.currentLevelName}
        shopId={drawerContext?.shopId || (currentShopId ? String(currentShopId) : undefined)}
        onSuccess={() => {
          fetchMemberInfo();
          memberInfoRef.current?.refresh();
        }}
      />
      <ChangePointDrawer
        open={pointDrawerOpen}
        onClose={() => setPointDrawerOpen(false)}
        oneId={pointDrawerContext?.oneId || ''}
        memberCardId={pointDrawerContext?.memberCardId}
        shopId={pointDrawerContext?.shopId}
        channelType={pointDrawerContext?.channelType}
        mobile={pointDrawerContext?.mobile}
        platformId={pointDrawerContext?.platformId}
        currentPoint={pointDrawerContext?.currentPoint}
        onSuccess={() => {
          setPointDrawerOpen(false);
          fetchMemberInfo();
          memberInfoRef.current?.refresh();
        }}
      />
    </div>
  );
};

export default MemberDetail;
